import React from 'react';
import * as Inovx from '@shared';
import KnowledgeBookActions from '../actions/KnowledgeBookActions';
import ViewerActions from '../actions/ViewerActions';
import ComponentHelpers from '../helpers/ComponentHelpers';
import { isScanPositionView } from '../helpers/viewMedia';
import ViewerStore from '../stores/ViewerStore';
import FacilityActions from './../actions/FacilityActions';
import OntologyActions from '../actions/OntologyActions';
import FacilityStore from './../stores/FacilityStore';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import { facilityMapContentLayers } from '../Interfaces/ViewerInterfaces';
import {
    canOpenKnowledgeViewEditPanel,
    FACILITY_SLIDEOUT_PANEL_IDS,
    isFacilitySlideoutPanelFloating,
    shouldRestoreFacilitySlideoutPanelOpen,
} from '../helpers/facilitySlideoutPanelSession';
const UrlUtilities = Inovx.Shared.Common.UrlUtilities;

declare var InovxLoader: any;

interface ViewerSidebarProps { }

interface ViewerSidebarState {
    sidebarOpen?: boolean;
    selectedFacility?: IFacilityDetail;
    selectedOntology?: IOntologyPart;
    isPanoViewerVisible?: boolean;
    isPointViewerVisible?: boolean;
    is3DViewerVisible?: boolean;
    isInRangeView?: boolean;
    kbBarOpen?: boolean;
    kvEditBarOpen?: boolean;
    facilityAssetSetsBarOpen?: boolean;
    assetSetVisualizationBarOpen?: boolean;
    isPanoRotationValid?: boolean;
    isPanoScanPosition?: boolean;
    measurementBarOpen?: boolean;
    assemblyLibraryBarOpen?: boolean;
    assetTagbarOpen?: boolean;
    showMap?: boolean;
    selectItem?: IAssetTag;
    updatedAnnotation?: IAssetTag;
    displayMode?: DisplayMode;
    occludedTags: string[];
    filteredAssets: string[];
    selectedAssembly: string;
    selectedKnowledgeView?: IKnowledgeViewDetail;
    selectedKnowledgeBook?: IKnowledgeBook;
    selectedOntologyTypes?: string[];
    rootOntologyTypes?: string[];
    isKBModalVisible?: boolean;
}

type CustomAnnotationClass = IAssetClassDetail & {
    SchemaID: string;
    TagDisplaySettings: ITagDisplaySettings;
};

function getViewerState(): ViewerSidebarState {
    const facility = FacilityStore.getSelectedFacility();
    const selectAnnID = ViewerStore.getSelectedAnnotation();
    const selectItem = facility?.AssetTags?.find((ann) => ann.ID === selectAnnID) || null;
    const selectedViewImage = ViewerStore.getSelectedViewImage();

    const isPanoRotationValid = !!(
        selectedViewImage?.Rotation &&
        selectedViewImage.Rotation.X != null &&
        selectedViewImage.Rotation.Y != null &&
        selectedViewImage.Rotation.Z != null &&
        selectedViewImage.Rotation.W != null
    );

    return {
        selectedFacility: FacilityStore.getSelectedFacility(),
        selectedOntology: FacilityStore.getSelectedOntology(),
        sidebarOpen: FacilityStore.getSidebarOpen(),
        kbBarOpen: FacilityStore.getKnowledgeBookbarOpen(),
        kvEditBarOpen: FacilityStore.getKnowledgeViewEditBarOpen(),
        facilityAssetSetsBarOpen: FacilityStore.getFacilityAssetSetsBarOpen(),
        assetSetVisualizationBarOpen: FacilityStore.getAssetSetVisualizationBarOpen(),
        isPanoViewerVisible: ViewerStore.isPanoViewerVisible(),
        isPointViewerVisible: ViewerStore.isOpenPointCloud(),
        is3DViewerVisible: ViewerStore.is3DViewerVisible(),
        isInRangeView: ViewerStore.isInRangeView(),
        assetTagbarOpen: FacilityStore.getAssetTagbarOpen(),
        measurementBarOpen: FacilityStore.getMeasurementbarOpen(),
        assemblyLibraryBarOpen: FacilityStore.getAssemblyLibrarybarOpen(),
        showMap: ViewerStore.isMiniMapOpen(),
        selectItem,
        updatedAnnotation: ViewerStore.getUpdatedAnnotation() as IAssetTag,
        displayMode: ViewerStore.getDisplayMode(),
        occludedTags: ViewerStore.getOccludedAnnotations(),
        filteredAssets: ViewerStore.getFilteredAssets(),
        selectedAssembly: ViewerStore.getSelectedAssembly(),
        selectedKnowledgeView: ViewerStore.getSelectedKnowledgeView(),
        selectedKnowledgeBook: KnowledgeBookStore.getSelectedKnowledgeBook(),
        selectedOntologyTypes: FacilityStore.getSelectedOntologyPartContentTypes(),
        rootOntologyTypes: FacilityStore.getRootOntologyContentTypes(),
        isKBModalVisible: KnowledgeBookStore.getKBModalVisible(),
        isPanoRotationValid,
        isPanoScanPosition: isScanPositionView(selectedViewImage)
    };
}

const ViewerSidebar: React.FC<ViewerSidebarProps> = () => {
    const [storeState, setStoreState] = React.useState<ViewerSidebarState>({ ...getViewerState() });
    const [selectedMenu, setSelectedMenu] = React.useState<string>('');
    const [selectedTopMenu, setSelectedTopMenu] = React.useState<string>('');
    const [isCustomAnnotationMenuOpen, setIsCustomAnnotationMenuOpen] = React.useState(false);
    const [lastCustomAnnotationClassID, setLastCustomAnnotationClassID] = React.useState<string | null>(null);
    const [expandedMenu, setExpandedMenu] = React.useState<string>('');

    const baseUrl = React.useMemo(() => UrlUtilities.getBaseApplicationUrl(), []);
    const homeHref = React.useMemo(
        () => `/Home${storeState.sidebarOpen ? '?expand=true' : ''}`,
        [storeState.sidebarOpen]
    );

    const querySearchRef = React.useRef<string>(''); // remember last query string without re-rendering
    const submenuRef = React.useRef<HTMLLIElement | null>(null);
    const customAnnotationMenuRef = React.useRef<HTMLLIElement | null>(null);

    // mounted guard to avoid setState after unmount
    const mountedRef = React.useRef(true);
    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // subscribe to stores
    React.useEffect(() => {
        const handler = () => {
            if (!mountedRef.current) return;
            setStoreState(getViewerState());
        };
        FacilityStore.addChangeListener(handler);
        ViewerStore.addChangeListener(handler);
        KnowledgeBookStore.addChangeListener(handler);
        return () => {
            FacilityStore.removeChangeListener(handler);
            ViewerStore.removeChangeListener(handler);
            KnowledgeBookStore.removeChangeListener(handler);
        };
    }, []);

    // On mount: close slideouts (other than asset tag bar)
    React.useEffect(() => {
        setTimeout(() => {
            if (
                storeState.kbBarOpen &&
                !shouldRestoreFacilitySlideoutPanelOpen(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook)
            ) {
                FacilityActions.closeKBbar();
            }
            if (storeState.facilityAssetSetsBarOpen &&
                !shouldRestoreFacilitySlideoutPanelOpen(FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets)) {
                FacilityActions.closeFacilityAssetSetsBar();
            }
            if (storeState.assetSetVisualizationBarOpen) {
                FacilityActions.closeAssetSetVisualizationBar();
            }
            if (storeState.measurementBarOpen) {
                FacilityActions.toggleMeasurementBar(false);
            } 
            if (storeState.assemblyLibraryBarOpen) {
                FacilityActions.toggleAssemblyLibraryBar(false);
            }
        }, 0);
    }, []);

    React.useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, [storeState.sidebarOpen]);

    // On mount: check expand flag
    React.useEffect(() => {
        const urlObject = new URL(window.location.href);
        const searchParams = new URLSearchParams(urlObject.search);
        const isExpand = searchParams.get('expand');
        if (isExpand === 'true') {
            setTimeout(() => {
                FacilityActions.openSidebar();
            }, 0);
            querySearchRef.current = '?expand=true';
            toggleMainContainerClass('view-expanded', 'view-collapsed');
        }
    }, []);

    const toggleMainContainerClass = (addClass = '', removeClass = '') => {
        const elements = document.getElementsByClassName('facility-app-container');
        for (let i = 0; i < elements.length; i++) {
            elements[i].classList.add(addClass);
            elements[i].classList.remove(removeClass);
        }
    };

    const toggleSidebar = () => {
        if (storeState.sidebarOpen) {
            toggleMainContainerClass('view-collapsed', 'view-expanded');
            FacilityActions.closeSidebar();
            querySearchRef.current = FacilityActions.updateUrlWithExpanded('false');
        } else {
            toggleMainContainerClass('view-expanded', 'view-collapsed');
            FacilityActions.openSidebar();
            querySearchRef.current = FacilityActions.updateUrlWithExpanded('true');
        }
    };

    const hidePopup = () => {
        if (KnowledgeBookStore.getKVModalVisible()) {
            KnowledgeBookActions.finishKnowledgeViewEdit();
        }
        if (KnowledgeBookStore.getKBModalVisible()) {
            KnowledgeBookActions.finishEdit();
        }
    }

    const hideSlideouts = (isOpeningOtherSlideout: boolean, restoreKnowledgeView: boolean = true) => {
        let closed = false;
        if (storeState.measurementBarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.measurements)) {
            FacilityActions.toggleMeasurementBar(false);
        }
        if (storeState.assemblyLibraryBarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.assemblyLibrary)) {
            FacilityActions.toggleAssemblyLibraryBar(false);
        }
        if (storeState.facilityAssetSetsBarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets)) {
            FacilityActions.closeFacilityAssetSetsBar(restoreKnowledgeView);
        }
        if (storeState.assetSetVisualizationBarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.assetSetVisualization)) {
            FacilityActions.closeAssetSetVisualizationBar();
        }
        if (isOpeningOtherSlideout) {
            if (storeState.kbBarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook)) {
                FacilityActions.closeKBbar();
            }
            if (storeState.assetTagbarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.assetTags)) {
                FacilityActions.toggleAssetTagBar(false);
            }
        }

        return closed;
    };

    const hasUnsavedData = (continuation?: () => void | Promise<unknown>): boolean => {
        return ViewerActions.checkForKnowledgeViewChanges(continuation);
    }

    const hasUnsavedKBData = (): boolean => {
        if (KnowledgeBookStore.getHasChanges()) {
            return true;
        } else if (KnowledgeBookStore.getKBModalVisible()) {
            KnowledgeBookActions.finishEdit();
        }
    }

    const toggleAssetTagbar = () => {
        if (storeState.assetTagbarOpen && isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.assetTags)) {
            FacilityActions.toggleAssetTagBar(false);
            return;
        }

        if (hideSlideouts(true)) return;
        if (!storeState.assetTagbarOpen) {
            FacilityActions.toggleAssetTagBar(true);
        }
    };

    const toggleMeasurements = () => {
        if (storeState.measurementBarOpen && isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.measurements)) {
            FacilityActions.toggleMeasurementBar(false);
            return;
        }

        if (hideSlideouts(true)) return;
        if (!storeState.measurementBarOpen) {
            FacilityActions.toggleMeasurementBar(true);
        }
    };

    const toggleKBbar = () => {
        if (storeState.kbBarOpen && isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook)) {
            FacilityActions.closeKBbar();
            return;
        }

        if (!storeState.kbBarOpen && storeState.facilityAssetSetsBarOpen) {
            FacilityActions.closeFacilityAssetSetsBar();
        }
        if (hideSlideouts(true)) return;
        if (!storeState.kbBarOpen) {
            const shouldReopenKnowledgeViewEdit = !storeState.kvEditBarOpen &&
                ViewerStore.isEditingKnowledgeView() &&
                canOpenKnowledgeViewEditPanel(
                    ComponentHelpers.hasModifyKnowledgeBookPermission(storeState.selectedKnowledgeBook),
                    storeState.selectedKnowledgeBook?.ID,
                    storeState.selectedKnowledgeView?.ID
                );

            if (shouldReopenKnowledgeViewEdit) {
                FacilityActions.openKnowledgeViewEditBar();
            } else {
                FacilityActions.openKBbar();
            }
        }
    };

    const toggleFacilityAssetSetsBar = () => {
        if (hasUnsavedData(toggleFacilityAssetSetsBar)) return;
        if (storeState.facilityAssetSetsBarOpen && isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets)) {
            FacilityActions.closeFacilityAssetSetsBar();
            return;
        }

        if (!hasFacilityAssetSetsAccess) return;
        if (!storeState.facilityAssetSetsBarOpen && storeState.kbBarOpen) {
            FacilityActions.closeKBbar();
        }
        if (hideSlideouts(true)) return;
        if (!storeState.facilityAssetSetsBarOpen) {
            FacilityActions.openFacilityAssetSetsBar();
        }
    };

    const toggleAssetSetVisualizationBar = () => {
        if (storeState.assetSetVisualizationBarOpen && isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.assetSetVisualization)) {
            FacilityActions.closeAssetSetVisualizationBar();
            return;
        }

        if (!hasFacilityAssetSetsAccess) return;
        if (hideSlideouts(true)) return;
        if (!storeState.assetSetVisualizationBarOpen) {
            FacilityActions.openAssetSetVisualizationBar();
        }
    };

    const toggleAssemblyLibrary = () => {
        if (ViewerStore.getSelectedKnowledgeView() && !ViewerActions.canAuthorKnowledgeView()) return;

        // Assemblies must be visible while authoring so that placed instances
        // can be located. Match the existing Asset Tags confirmation flow.
        if (!ViewerStore.getContentLayers().assemblies) {
            Inovx.Shared.System.Actions.displayConfirmationMessage({
                message: InovxLoader.t('facility:assemblyPlacement.message'),
                confirmCallback: () => {
                    ViewerActions.setLayerVisible('assemblies');
                    if (!FacilityStore.getAssemblyLibrarybarOpen()) {
                        if (hideSlideouts(true)) return;
                        FacilityActions.toggleAssemblyLibraryBar(true);
                    }
                },
            });
            return;
        }

        if (storeState.assemblyLibraryBarOpen && isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.assemblyLibrary)) {
            FacilityActions.toggleAssemblyLibraryBar(false);
            return;
        }

        // Keep the existing sidebar toggle behavior for a visible, docked
        // panel: clicking it closes the panel rather than reopening it.
        if (storeState.assemblyLibraryBarOpen) {
            if (hideSlideouts(true)) return;
            return;
        }

        if (hideSlideouts(true)) return;
        FacilityActions.toggleAssemblyLibraryBar(true);
    };

    const openFacilityMap = async () => { 
        if (hasUnsavedData(openFacilityMap)) return;
        if (hasUnsavedKBData()) return;
        if (hideSlideouts(false)) return;
        hidePopup();
        if (FacilityStore.getKnowledgeLibraryOpen()) {
            FacilityActions.closeKnowledgeLibrary();
        }
        ViewerActions.resetCameraProperties();
        ViewerActions.hidePanoViewerImage();
        ViewerActions.setContentLayers(facilityMapContentLayers);

        KnowledgeBookActions.resetKnowledgeViews();
        ViewerActions.closeKnowledgeView();
        KnowledgeBookActions.closeKnowledgeBook();
        FacilityActions.closeMediaGallery();
    };

    const open3DView = async () => {
        if (hasUnsavedData(open3DView)) return;
        if (hasUnsavedKBData()) return;
        if (hideSlideouts(false)) return;
        hidePopup();
        const ontology = FacilityStore.getSelectedOntology();
        setSelectedTopMenu('');
        await FacilityActions.openLODViewer(ontology.ID, ["Point Cloud Data", "CAD Data", "Mesh Data"]);
    };

    const openRangeView = async () => {
        if (hasUnsavedData(openRangeView)) return;
        if (hideSlideouts(false)) return;
        setSelectedTopMenu('');
        const view = ViewerStore.getSelectedViewImage();
        const ontology = FacilityStore.getRootOntologyPart();
        const measurements = ViewerStore.getMeasurements();

        await OntologyActions.selectOntology(ontology);
        await FacilityActions.openLODViewer(ontology.ID, ['Point Cloud Data', 'CAD Data', "Mesh Data"], 30.0, view?.Position, .001, false, view);

        await ViewerActions.updateMeasurements(measurements);
        /*
        setTimeout(() => {
            for (const m of measurements) {
                ViewerActions.updateMeasurements(m);
            }
        }, 0);
        */
    };

    const knowledgeBookActive = !!storeState.selectedKnowledgeBook;
    const hasManageContentPermission: boolean = ComponentHelpers.hasManageContentPermission();

    //modify and manage tags permissions
    const hasModifyTagsPermission: boolean = ComponentHelpers.hasModifyTagsPermission();
    const hasManageTagsPermission: boolean = ComponentHelpers.hasManageTagsPermission();

    const hasModifyKBPermission: boolean = ComponentHelpers.hasModifyKnowledgeBookPermission(storeState.selectedKnowledgeBook);
    const hasFacilityAssetSetsAccess: boolean = ComponentHelpers.hasFacilityExplorerPermission();

    const _createTag = (withBox: boolean) => {
        if (storeState.displayMode != DisplayMode.CreateAnnotation) {
            if (ViewerActions.hasHiddenAssetTags()) {
                Inovx.Shared.System.Actions.displayConfirmationMessage({
                    message: InovxLoader.t('facility:assetTagPlacement.message'),
                    //confirmButtonText: InovxLoader.t('facility:assetTagPlacement.continue'),
                    confirmCallback: () => {
                        const annotationType = withBox ? AnnotationType.AssetTagWithBox : AnnotationType.AssetTag;
                        if (ViewerActions.setCreateAnnotationDisplayMode(annotationType)) {
                            ViewerActions.displayAllAssetTags();
                        }
                    },
                });
                return;
            }
            ViewerActions.setCreateAnnotationDisplayMode(withBox ? AnnotationType.AssetTagWithBox : AnnotationType.AssetTag);
        } else {
            ViewerActions.resetDisplayMode();
        }
    };

    const setMeasurementMode = (mode: MeasurementType) => {
        if (ViewerStore.getSelectedAnnotation() != null) ViewerActions.resetAnnotation();
        if (ViewerStore.getSelectedAssembly() != null) ViewerActions.resetAssemblySelection();
        ViewerActions.setMeasurementCreateMode(mode);
    };

    const layers = ViewerStore.getContentLayers();
    const toggleLayer = (key: keyof typeof layers) => ViewerActions.toggleLayer(key);

    const handleAssetsLayerToggle = () => {
        // Combined current state of "Assets/Mesh" layer
        const current = layers.assets || layers.mesh;
        const newValue = !current; // what both should be after the click

        if (layers.assets !== newValue) {
            ViewerActions.toggleLayer('assets');
        }

        if (layers.mesh !== newValue) {
            ViewerActions.toggleLayer('mesh');
        }

        /*
        // Only toggle if the layer actually needs to change
        if (allowedCoreKeys.includes('assets') && layers.assets !== newValue) {
            ViewerActions.toggleLayer('assets');
        }

        if (allowedCoreKeys.includes('mesh') && layers.mesh !== newValue) {
            ViewerActions.toggleLayer('mesh');
        }
        */
    };

    React.useEffect(() => {
        if (!layers.assemblies && storeState.assemblyLibraryBarOpen) {
            FacilityActions.toggleAssemblyLibraryBar(false);
        }
    }, [layers]);

    const toggleMiniMap = () => {
        ViewerActions.setMiniMapOpen(!storeState.showMap);
    };

    const toggleBottomMenu = (menu: string) => {
        if (menu === 'measure') {
            if (selectedMenu === 'measure') {
                setSelectedMenu('');
                ViewerActions.resetDisplayMode();
            } else {
                setSelectedMenu('measure');
            }
        } else if (menu === 'tag') {
            if (selectedMenu === 'tag') {
                setSelectedMenu('');
                ViewerActions.resetDisplayMode();
            } else {
                setSelectedMenu('tag');
            }
        }  else if (menu === 'edit-knowledge-view-3d') {
            if (selectedMenu === 'edit-knowledge-view-3d') {
                setSelectedMenu('');
            } else {
                setSelectedMenu('edit-knowledge-view-3d');
            }
        } else if (menu === '360ImageView') {
            if (selectedTopMenu === '360ImageView') {
                setSelectedTopMenu('');
            } else {
                if (storeState.isInRangeView) {
                    const view = ViewerStore.getRangeView();
                    ViewerActions.showPanoViewerImage(view);
                    setSelectedTopMenu('');
                } else {
                    setSelectedTopMenu('360ImageView');
                }
            }
        } else if (menu === 'rangeView') {
            if (selectedTopMenu === 'rangeView') {
                setSelectedTopMenu('');
            } else {
                setSelectedTopMenu('rangeView');
            }
        }
    };

    const allowedCoreKeys = React.useMemo(() => {
        return ViewerActions.getCoreKeys(storeState.selectedOntologyTypes);
    }, [storeState.selectedOntologyTypes]);

    const allOntologyCoreKeys = React.useMemo(() => {
        return ViewerActions.getCoreKeys(storeState.rootOntologyTypes);
    }, [storeState.rootOntologyTypes]);

    React.useEffect(() => {
        if (storeState.selectedAssembly && selectedMenu !== 'edit-assembly') {
            toggleBottomMenu('edit-assembly');
        } else if (!storeState.selectedAssembly && selectedMenu === 'edit-assembly') {
            toggleBottomMenu('edit-assembly');
        }
    }, [storeState.selectedAssembly]);

    const changeCreateAnnotationMode = (annotationMode: AnnotationType, annotationClassID?: string) => {
        if (annotationClassID) {
            setLastCustomAnnotationClassID(annotationClassID);
        }
        setIsCustomAnnotationMenuOpen(false);
        ViewerActions.setCreateAnnotationDisplayMode(annotationMode, annotationClassID);
    }

    const openKnowledgeLibrary = async () => {
        if (hasUnsavedData(openKnowledgeLibrary)) return;
        if (hasUnsavedKBData()) return;
        if (hideSlideouts(true, false)) return;
        hidePopup();

        ViewerActions.resetCameraProperties();
        ViewerActions.hidePanoViewerImage();

        KnowledgeBookActions.resetKnowledgeViews();
        ViewerActions.closeKnowledgeView();
        KnowledgeBookActions.closeKnowledgeBook();

        await FacilityActions.openKnowledgeLibrary();
    };

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                submenuRef.current &&
                !submenuRef.current.contains(event.target as Node)
            ) {
                setSelectedTopMenu('');
            }

            if (
                customAnnotationMenuRef.current &&
                !customAnnotationMenuRef.current.contains(event.target as Node)
            ) {
                setIsCustomAnnotationMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    React.useEffect(() => {
        if (selectedMenu !== 'edit-knowledge-view-3d') {
            setIsCustomAnnotationMenuOpen(false);
        }
    }, [selectedMenu]);

    const contentTypes = FacilityStore.getSelectedOntologyPartContentTypes() || [];
    var canOpen3D = contentTypes.some(t =>
        t === 'Assets' || t === 'CAD Data' || t === 'Point Cloud Data' || t === 'Mesh Data'
    );
    const isAssemblyContextMenuVisible = () => {
        if (ViewerStore.getSelectedKnowledgeView() && !ViewerActions.canAuthorKnowledgeView()) return false;
        if (ViewerStore.getSelectedKnowledgeView()) {
            return ComponentHelpers.hasModifyKnowledgeBookPermission(storeState.selectedKnowledgeBook);
        } else {
            return ComponentHelpers.hasManageContentPermission();
        }
    }

    const shouldShowMeasurementBarButton =
        !storeState.isPanoViewerVisible || storeState.isPanoRotationValid;

    const shouldShowTagMenu =
        (!ViewerStore.getSelectedKnowledgeView() || ViewerActions.canAuthorKnowledgeView()) &&
        shouldShowMeasurementBarButton &&
        (!storeState.isPanoViewerVisible || storeState.isPanoScanPosition) &&
        (storeState.isPanoViewerVisible || storeState.is3DViewerVisible) &&
        (hasModifyTagsPermission || hasManageTagsPermission);

    const shouldShowKnowledgeAnnotationMenu = ViewerActions.canAuthorKnowledgeView() && hasModifyKBPermission;

    const customAnnotationClasses = React.useMemo(() => {
        const annotationClasses = (storeState.selectedFacility?.AnnotationClasses || [])
            .filter((annotationClass) => !annotationClass.IsSystem) as CustomAnnotationClass[];

        if (annotationClasses.length > 0) {
            annotationClasses.push({
                SchemaID: storeState.selectedFacility?.SchemaID || '',
                TagDisplayData: null,
                TagDisplaySettings: null,
                ID: 'standard',
                Name: 'ObservationTag',
                IsSystem: true,
                AttributeDefinitions: [],
                HierarchyAttributeDefinitions: []
            });
        }

        return annotationClasses.sort((a, b) => a.Name.localeCompare(b.Name));
    }, [storeState.selectedFacility?.AnnotationClasses]);

    const selectedCustomAnnotationClass = React.useMemo(() => {
        return customAnnotationClasses.find((annotationClass) => annotationClass.ID === lastCustomAnnotationClassID) || null;
    }, [customAnnotationClasses, lastCustomAnnotationClassID]);

    React.useEffect(() => {
        if (!customAnnotationClasses.length) {
            if (lastCustomAnnotationClassID !== null) {
                setLastCustomAnnotationClassID(null);
            }
            return;
        }
        if (!customAnnotationClasses.some((annotationClass) => annotationClass.ID === lastCustomAnnotationClassID)) {
            setLastCustomAnnotationClassID("standard");
        }
    }, [customAnnotationClasses, lastCustomAnnotationClassID]);

    const shouldShowManageAssetSets =
        hasFacilityAssetSetsAccess &&
        (!!storeState.selectedOntology?.ID || !!storeState.isPanoViewerVisible) &&
        (!!storeState.is3DViewerVisible || !!storeState.isPanoViewerVisible) &&
        (!storeState.isPanoViewerVisible || storeState.isPanoScanPosition) &&
        !storeState.isInRangeView;

    const shouldShowDisplayVisualization =
        hasFacilityAssetSetsAccess &&
        (!!storeState.selectedOntology?.ID || !!storeState.isPanoViewerVisible) &&
        (!!storeState.is3DViewerVisible || !!storeState.isPanoViewerVisible) &&
        (!storeState.isPanoViewerVisible || storeState.isPanoScanPosition) &&
        !storeState.isInRangeView &&
        !knowledgeBookActive;

    React.useEffect(() => {
        if (knowledgeBookActive && storeState.assetSetVisualizationBarOpen) {
            FacilityActions.closeAssetSetVisualizationBar();
        }
    }, [knowledgeBookActive, storeState.assetSetVisualizationBarOpen]);

    const shouldShowMeasureMenu = shouldShowMeasurementBarButton;

    const shouldShowBottomSeparator =
        shouldShowTagMenu || shouldShowKnowledgeAnnotationMenu || shouldShowMeasureMenu;

    const openMediaGallery = () => {
        if (hasUnsavedData(openMediaGallery)) return;
        if (hasUnsavedKBData()) return;
        if (hideSlideouts(true)) return;
        hidePopup();

        ViewerActions.resetCameraProperties();
        ViewerActions.hidePanoViewerImage();
        ViewerActions.setContentLayers(facilityMapContentLayers);

        KnowledgeBookActions.resetKnowledgeViews();
        ViewerActions.closeKnowledgeView();
        KnowledgeBookActions.closeKnowledgeBook();
        FacilityActions.openMediaGallery();
    };

    const toggleSubmenu = () => setExpandedMenu(v => v === 'assetSets' ? '' : 'assetSets');

    const renderLastAnnotationMenu = () => {
        if(selectedCustomAnnotationClass && selectedCustomAnnotationClass.Name !== "ObservationTag") {
            return (
                <li className="sub-menu-icon custom-annotation">
                    <button
                        type="button"
                        className="btn-icon"
                        title={InovxLoader.t('facility:action.customAnnotation').replace('__name__', selectedCustomAnnotationClass.Name)}
                        onClick={() => changeCreateAnnotationMode(AnnotationType.ObservationTag, selectedCustomAnnotationClass.ID)}
                        disabled={false}
                        style={{ backgroundColor: selectedCustomAnnotationClass.TagDisplaySettings.TagColor, color: selectedCustomAnnotationClass.TagDisplaySettings.FontColor }}
                    >T</button>
                </li>
            )
        }
        return (
            <li className="sub-menu-icon">
                <button
                    className="btn-icon text-annotation"
                    title={InovxLoader.t('facility:action.textAnnotation')}
                    onClick={() => changeCreateAnnotationMode(AnnotationType.ObservationTag)}
                    disabled={false}
                ></button>
            </li>
        )
    }
    return (
        <div id="sidebar" className={`sidebar ${storeState.sidebarOpen ? 'show' : ''}`}>
            <div className="d-flex f-dc j-csb h-100p">
                <nav className="nav top-menu">
                    <ul className="list-unstyled components">
                        <li>
                            <a className="d-flex j-csb" onClick={() => toggleSidebar()}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/menu_icon.png`} />
                                </div>
                            </a>
                        </li>
                        <li>
                            <a href={homeHref} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.home')}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/home_icon.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.home')}</div>
                            </a>
                        </li>
                        <li>
                            <a onClick={() => openFacilityMap()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.facilityMap')}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/FacilityMap.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.facilityMap')}</div>
                            </a>
                        </li>
                        {canOpen3D && storeState.isPanoViewerVisible && (
                            <li className={`sub-menu active ${selectedTopMenu === '360ImageView' ? 'active-submenu' : ''}`} ref={submenuRef}>
                                <a onClick={() => toggleBottomMenu('360ImageView')} className={`main-menu ${selectedTopMenu === '360ImageView' ? "" : "v-tooltip"}`} aria-label={InovxLoader.t('facility:menu.360ImageView')}>
                                    <div className="icon">
                                        <img src={`${baseUrl}/images/menus/360Image.png`} />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('facility:menu.360ImageView')}</div>
                                </a>
                                <ul className="submenu-content" >
                                    {!knowledgeBookActive && <li>
                                        <a onClick={() => openRangeView()}>
                                            <div className="icon">
                                                <img src={`${baseUrl}/images/menus/GoTo_3D.png`} />
                                            </div>
                                            <div className="nav_title">{InovxLoader.t('facility:menu.rangeView')}</div>
                                        </a>
                                    </li>}
                                    <li>
                                        <a onClick={() => open3DView()}>
                                            <div className="icon">
                                                <img src={`${baseUrl}/images/menus/3d_model.png`} />
                                            </div>
                                            <div className="nav_title">{InovxLoader.t('facility:menu.facilityModel')}</div>
                                        </a>
                                    </li>
                                </ul>
                            </li>
                        )}
                        {storeState.isInRangeView && (
                            <li className={`sub-menu active ${selectedTopMenu === 'rangeView' ? 'active-submenu' : ''}`} ref={submenuRef} >
                                <a onClick={() => toggleBottomMenu('rangeView')} className={`main-menu ${selectedTopMenu === 'rangeView' ? "" : "v-tooltip"}`} aria-label={InovxLoader.t('facility:menu.rangeView')}>
                                    <div className="icon">
                                        <img src={`${baseUrl}/images/menus/GoTo_3D.png`} />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('facility:menu.rangeView')}</div>
                                </a>
                                <ul className="submenu-content">
                                    <li>
                                        <a onClick={() => toggleBottomMenu('360ImageView')}>
                                            <div className="icon">
                                                <img src={`${baseUrl}/images/menus/360Image.png`} />
                                            </div>
                                            <div className="nav_title">{InovxLoader.t('facility:menu.360ImageView')}</div>
                                        </a>
                                    </li>
                                    {canOpen3D && <li>
                                        <a onClick={() => open3DView()}>
                                            <div className="icon">
                                                <img src={`${baseUrl}/images/menus/3d_model.png`} />
                                            </div>
                                            <div className="nav_title">{InovxLoader.t('facility:menu.facilityModel')}</div>
                                        </a>
                                    </li>}
                                </ul>
                            </li>
                        )}
                       
                        {(!storeState.isInRangeView && storeState.is3DViewerVisible) &&
                            <li className={storeState.is3DViewerVisible ? 'active' : ''}>
                                <a onClick={() => open3DView()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.facilityModel')}>
                                    <div className="icon">
                                        <img src={`${baseUrl}/images/menus/3d_model.png`} />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('facility:menu.facilityModel')}</div>
                                </a>
                            </li>
                        }
                        <li>
                            <a className="v-tooltip" aria-label={InovxLoader.t('facility:menu.knowledgeLibrary')} onClick={() => openKnowledgeLibrary()}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/knowledge_library.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.knowledgeLibrary')}</div>
                            </a>
                        </li>
                       
                        <li className={selectedMenu === 'media-gallery' ? 'active' : ''}>
                            <a className="v-tooltip" aria-label={InovxLoader.t('facility:menu.mediaGallery')} onClick={() => openMediaGallery()}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/mediaGallery.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.mediaGallery')}</div>
                            </a>
                        </li>
                        
                        <li>
                            <hr style={{ width: '90%', margin: '8px auto' }} />
                        </li>
                        {shouldShowManageAssetSets && (
                            <li className={storeState.facilityAssetSetsBarOpen ? 'active' : ''}>
                                <a onClick={() => toggleFacilityAssetSetsBar()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.manageAssetSets')}>
                                    <div className="icon">
                                        <img src={`${baseUrl}/images/menus/assetSetsTree.png`} />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('facility:menu.manageAssetSets')}</div>
                                </a>
                            </li>
                        )}
                        {(knowledgeBookActive && (storeState.isPanoViewerVisible || storeState.is3DViewerVisible)) && (
                            <li className={(storeState.kbBarOpen || storeState.kvEditBarOpen) ? 'active' : ''}>
                                <a onClick={() => toggleKBbar()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.knowledgeViews')}>
                                    <div className="icon">
                                        <img src={`${baseUrl}/images/menus/KV.png`} />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('facility:menu.knowledgeViews')}</div>
                                </a>
                            </li>
                        )}
                        {shouldShowDisplayVisualization && (
                            <li className={storeState.assetSetVisualizationBarOpen ? 'active' : ''}>
                                <a onClick={() => toggleAssetSetVisualizationBar()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.displayVisualization')}>
                                    <div className="icon">
                                        <img src={`${baseUrl}/images/menus/displayLayers.png`} />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('facility:menu.displayVisualization')}</div>
                                </a>
                            </li>
                        )}

                        <li className={storeState.assetTagbarOpen ? 'active' : ''}>
                            <a onClick={() => toggleAssetTagbar()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.searchAssets')}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/AssetRegistry.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.searchAssets')}</div>
                            </a>
                        </li>
                        
                        {shouldShowMeasurementBarButton && (
                            <li className={storeState.measurementBarOpen ? 'active' : ''}>
                                <a onClick={() => toggleMeasurements()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.measurements')}>
                                    <div className="icon">
                                        <img src={`${baseUrl}/images/menus/measurements.png`} />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('facility:menu.measurements')}</div>
                                </a>
                            </li>
                        )}
                        {isAssemblyContextMenuVisible() && storeState.is3DViewerVisible && <li className={storeState.assemblyLibraryBarOpen ? 'active' : ''}>
                            <a onClick={() => toggleAssemblyLibrary()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.assemblies')}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/assemblies.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.assemblies')}</div>
                            </a>
                        </li>}
                    </ul>
                </nav>

                <nav className="nav bottom-sidebar">
                    <ul className="list-unstyled components">
                        <li className={storeState.showMap ? 'active-menu' : ''}>
                            <a onClick={() => toggleMiniMap()} className="v-tooltip" aria-label={InovxLoader.t('facility:menu.miniMap')}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/miniMap.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.miniMap')}</div>
                            </a>
                        </li>
                        {shouldShowBottomSeparator && (
                            <li>
                                <hr style={{ width: '90%', margin: '8px auto' }} />
                            </li>
                        )}
                        
                        {shouldShowTagMenu && <li className={`sub-menu ${selectedMenu === 'tag' ? 'active-submenu' : ''}`}>
                            <a onClick={() => toggleBottomMenu('tag')} className={selectedMenu === 'tag' ? "" : "v-tooltip"} aria-label={InovxLoader.t('facility:menu.assetTags')}>
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/tag_icon.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.assetTags')}</div>
                            </a>
                            <ul>
                                <>
                                    <li className="sub-menu-icon">
                                        <button className="btn-icon create-tag" title={InovxLoader.t("facility:action.createTag")} onClick={() => _createTag(false)} disabled={false}></button>
                                    </li>
                                    <li className="sub-menu-icon">
                                        <button className="btn-icon create-box-tag" title={InovxLoader.t("facility:action.createTagWithVolume")} onClick={() => _createTag(true)}></button>
                                    </li>
                                </>
                            </ul>
                        </li>}
                        {shouldShowKnowledgeAnnotationMenu && <li className={`sub-menu ${selectedMenu === 'edit-knowledge-view-3d' ? 'active-submenu' : ''}`}>
                            <a
                                onClick={() => toggleBottomMenu('edit-knowledge-view-3d')}
                                className={selectedMenu === 'edit-knowledge-view-3d' ? '' : 'v-tooltip'}
                                aria-label={InovxLoader.t('facility:menu.annotations')}
                            >
                                <div className="icon">
                                    <img src={`${baseUrl}/images/menus/Notes.png`} />
                                </div>
                                <div className="nav_name">{InovxLoader.t('facility:menu.annotations')}</div>
                            </a>
                            <ul>
                                <li className="sub-menu-icon">
                                    <button
                                        className="btn-icon information-annotation"
                                        title={InovxLoader.t('facility:action.informationAnnotation')}
                                        onClick={() => changeCreateAnnotationMode(AnnotationType.InformationAnnotation)}
                                        disabled={false}
                                    ></button>
                                </li>
                                <li className="sub-menu-icon">
                                    <button
                                        className="btn-icon link-annotation"
                                        title={InovxLoader.t('facility:action.linkAnnotation')}
                                        onClick={() => changeCreateAnnotationMode(AnnotationType.LinkAnnotation)}
                                        disabled={false}
                                    ></button>
                                </li>
                                {renderLastAnnotationMenu()}
                                
                                
                                {customAnnotationClasses.length > 0 && (
                                    <li className="last-sub-menu-icon" ref={customAnnotationMenuRef} style={{ position: 'relative' }}>
                                        <button
                                            type="button"
                                            className="btn-icon"
                                            title={InovxLoader.t('facility:action.selectCustomAnnotation')}
                                            onClick={() => setIsCustomAnnotationMenuOpen((current) => !current)}
                                        >
                                            ▶
                                        </button>
                                        {isCustomAnnotationMenuOpen && (
                                            <div className="custom-submenu">
                                                {customAnnotationClasses.filter((annotationClass) => annotationClass?.ID !== selectedCustomAnnotationClass?.ID).map((annotationClass) => (
                                                    <button
                                                        key={annotationClass.ID}
                                                        type="button"
                                                        onClick={() => changeCreateAnnotationMode(AnnotationType.ObservationTag, annotationClass.ID)}
                                                        className="custom-submenu-item"
                                                    >
                                                        {annotationClass.Name === 'ObservationTag' ? InovxLoader.t('facility:action.standard') : annotationClass.Name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </li>
                                )}
                            </ul>
                        </li>}
                        {shouldShowMeasurementBarButton && (
                            <li className={`sub-menu ${selectedMenu === 'measure' ? 'active-submenu' : ''}`}>
                                <a
                                    onClick={() => toggleBottomMenu('measure')}
                                    className={selectedMenu === 'measure' ? '' : 'v-tooltip'}
                                    aria-label={InovxLoader.t('facility:menu.measure')}
                                >
                                    <div className="icon">
                                        <img src={`${baseUrl}/images/measurement/add-measurement.png`} />
                                    </div>
                                    <div className="nav_name">{InovxLoader.t('facility:menu.measure')}</div>
                                </a>
                                <ul>
                                    <li className="sub-menu-icon">
                                        <button
                                            className="btn-icon create-measurement-point"
                                            title={InovxLoader.t('facility:action.createPoint')}
                                            onClick={() => setMeasurementMode(MeasurementType.Point)}
                                            disabled={false}
                                        ></button>
                                    </li>
                                    <li className="sub-menu-icon">
                                        <button
                                            className="btn-icon create-measurement-distance"
                                            title={InovxLoader.t('facility:action.createDistance')}
                                            onClick={() => setMeasurementMode(MeasurementType.Distance)}
                                            disabled={false}
                                        ></button>
                                    </li>
                                    <li className="sub-menu-icon">
                                        <button
                                            className="btn-icon create-measurement-ortho-north"
                                            title={InovxLoader.t('facility:action.createOrthoNorth')}
                                            onClick={() => setMeasurementMode(MeasurementType.ZOrtho)}
                                            disabled={false}
                                        ></button>
                                    </li>
                                    <li className="sub-menu-icon">
                                        <button
                                            className="btn-icon create-measurement-ortho-up"
                                            title={InovxLoader.t('facility:action.createOrthoUp')}
                                            onClick={() => setMeasurementMode(MeasurementType.YOrtho)}
                                            disabled={false}
                                        ></button>
                                    </li>
                                    <li className="sub-menu-icon">
                                        <button
                                            className="btn-icon create-measurement-ortho-east"
                                            title={InovxLoader.t('facility:action.createOrthoEast')}
                                            onClick={() => setMeasurementMode(MeasurementType.XOrtho)}
                                            disabled={false}
                                        ></button>
                                    </li>
                                </ul>
                            </li>
                        )}
                    </ul>
                </nav>
            </div>
        </div>
    );
};

export default ViewerSidebar;
